import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  LayoutGrid, User, Images, GraduationCap, Award, HeartHandshake, MessageSquareQuote, BookOpen, FileText,
  Building2, MessageCircle, BarChart3, ExternalLink, LogOut, Save, Eye, Send, Menu, KeyRound, Loader2, X,
} from "lucide-react";
import { useAdmin } from "./AdminContext";
import { PAGES } from "./pages";

export const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutGrid },
  { id: "profile", label: "Doctor Profile", icon: User },
  { id: "gallery", label: "Photos & Gallery", icon: Images },
  { id: "experience", label: "Experience & Qualifications", icon: GraduationCap },
  { id: "awards", label: "Awards & Achievements", icon: Award },
  { id: "services", label: "Services Available", icon: HeartHandshake },
  { id: "reviews", label: "Parent Reviews", icon: MessageSquareQuote },
  { id: "library", label: "Child Health Library", icon: BookOpen },
  { id: "content", label: "Website Content", icon: FileText },
  { id: "clinic", label: "Clinic Information", icon: Building2 },
  { id: "whatsapp", label: "WhatsApp Settings", icon: MessageCircle },
  { id: "analytics", label: "Website Analytics", icon: BarChart3 },
  { id: "account", label: "Account & Password", icon: KeyRound },
];

const Sidebar = ({ tab, go, onLogout }) => (
  <div className="flex h-full flex-col">
    <div className="flex items-center gap-3 px-5 py-5">
      <img src="/images/clinic-logo.jpg" alt="" className="h-11 w-11 rounded-full" />
      <div>
        <p className="font-display text-lg font-semibold leading-tight text-blue-900">Website Admin</p>
        <p className="text-xs text-slate-500">Dr Sayan's Kidz Clinic</p>
      </div>
    </div>
    <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
      {NAV.map((n) => (
        <button key={n.id} onClick={() => go(n.id)} data-testid={`admin-nav-${n.id}`}
          className={`flex w-full items-center gap-3 rounded-2xl px-3.5 py-2.5 text-left text-[15px] transition-colors ${tab === n.id ? "bg-sky-100 font-semibold text-blue-900" : "text-slate-700 hover:bg-slate-50"}`}>
          <n.icon className="h-[18px] w-[18px] shrink-0" /> {n.label}
        </button>
      ))}
    </nav>
    <div className="space-y-1 border-t border-slate-100 px-3 py-3">
      <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl px-3.5 py-2.5 text-[15px] text-slate-700 hover:bg-slate-50" data-testid="admin-view-live">
        <ExternalLink className="h-[18px] w-[18px]" /> View live website
      </a>
      <button onClick={onLogout} className="flex w-full items-center gap-3 rounded-2xl px-3.5 py-2.5 text-[15px] text-red-600 hover:bg-red-50" data-testid="admin-logout-btn">
        <LogOut className="h-[18px] w-[18px]" /> Logout
      </button>
    </div>
  </div>
);

const Status = ({ dirty, unpublished }) => {
  const [cls, text] = dirty ? ["bg-yellow-100 text-yellow-800", "Unsaved changes"]
    : unpublished ? ["bg-orange-100 text-orange-700", "Saved — not published yet"]
    : ["bg-green-100 text-green-700", "Website is up to date"];
  return <span data-testid="admin-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${cls}`}>{text}</span>;
};

const TopBar = ({ openMenu }) => {
  const { dirty, unpublished, busy, save, preview, publish } = useAdmin();
  const btn = "inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60 sm:px-4";
  return (
    <div className="sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-slate-100 bg-white/95 px-3 py-3 backdrop-blur sm:px-6">
      <div className="flex items-center gap-2">
        <button className="rounded-xl p-2 lg:hidden" onClick={openMenu} data-testid="admin-menu-btn" aria-label="Menu"><Menu className="h-5 w-5" /></button>
        <Status dirty={dirty} unpublished={unpublished} />
      </div>
      <div className="flex items-center gap-1.5 sm:gap-2">
        <button onClick={() => save()} disabled={!!busy} data-testid="admin-save-btn" className={`${btn} border border-slate-200 bg-white text-slate-800 hover:bg-slate-50`}>
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}<span className="hidden sm:inline">Save Changes</span>
        </button>
        <button onClick={preview} disabled={!!busy} data-testid="admin-preview-btn" className={`${btn} border border-slate-200 bg-white text-slate-800 hover:bg-slate-50`}>
          <Eye className="h-4 w-4" /><span className="hidden sm:inline">Preview</span>
        </button>
        <button onClick={publish} disabled={!!busy || (!dirty && !unpublished)} data-testid="admin-publish-btn" className={`${btn} bg-[#25D366] text-white hover:bg-[#1EBE5D]`}>
          {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span>Publish<span className="hidden sm:inline"> Changes</span></span>
        </button>
      </div>
    </div>
  );
};

export const AdminLayout = () => {
  const { tab = "dashboard" } = useParams();
  const navigate = useNavigate();
  const { content, onLogout } = useAdmin();
  const [menu, setMenu] = useState(false);
  const go = (id) => { navigate(`/admin/${id}`); setMenu(false); window.scrollTo(0, 0); };
  const Page = PAGES[tab] || PAGES.dashboard;

  return (
    <div className="min-h-screen bg-slate-50/70" data-testid="admin-panel">
      <aside className="fixed inset-y-0 left-0 hidden w-72 border-r border-slate-100 bg-white lg:block"><Sidebar tab={tab} go={go} onLogout={onLogout} /></aside>
      {menu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMenu(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">
            <button className="absolute right-3 top-5 rounded-xl p-2" onClick={() => setMenu(false)} aria-label="Close"><X className="h-5 w-5" /></button>
            <Sidebar tab={tab} go={go} onLogout={onLogout} />
          </aside>
        </div>
      )}
      <div className="lg:pl-72">
        <TopBar openMenu={() => setMenu(true)} />
        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
          {content ? <Page go={go} /> : <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-900" /></div>}
        </main>
      </div>
    </div>
  );
};
